import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import Phaser from 'phaser';
import { MainScene } from './scenes/main-scene';
import { PopupsManagerService } from '../../services/popups-manager.service';
import { PurchaseFlowComponent } from '../purchase-flow/purchase-flow.component';
import { BaseComponent } from '../../common/base/base.component';
import { Events } from './events';
import { EventKey } from '../../common/enums/event-key';
import { switchMap, takeUntil } from 'rxjs/operators';
import { Size } from '../../common/types/size';
import { TilesService } from '../../services/tiles.service';
import { ComplexWallItem } from '../../common/types/wall-item';
import { NftInformationComponent } from '../nft-information/nft-information.component';
import { AuthService } from '../../services/auth.service';
import { Debounce } from '../../common/decorators/debounce';
import { CommonService } from '../../services/common.service';
import { combineLatest } from 'rxjs';
import { ToolboxActionService } from '../../services/toolbox-action.service';

@Component({
  selector: 'app-wall',
  templateUrl: './wall.component.html',
  styleUrls: ['./wall.component.scss'],
})
export class WallComponent extends BaseComponent implements OnInit, OnDestroy {
  @ViewChild('container', { read: ElementRef, static: true })
  public container!: ElementRef<HTMLDivElement>;

  public phaser!: Phaser.Game;
  public size: Size = { w: 0, h: 0 };

  constructor(
    private authService: AuthService,
    private cartService: TilesService,
    private commonService: CommonService,
    private popupsManagerService: PopupsManagerService,
    private toolboxActionService: ToolboxActionService
  ) {
    super();
  }

  @HostListener('window:resize')
  @Debounce(100)
  public handleWindowResize(): void {
    this.size = this.calculateWallSize();
    this.updateCanvasSize();
    this.fetchAndSetZoom();
  }

  ngOnInit(): void {
    this.init();
    this.subscribeToEvents();
  }

  public openPurchaseFlow({
    clickedLeftSide,
    tilesSize,
  }: {
    clickedLeftSide: boolean;
    tilesSize: number;
  }): void {
    this.popupsManagerService.show(PurchaseFlowComponent, {
      className: clickedLeftSide ? 'popup-right' : '',
      inputs: {
        tilesSize,
      },
      outputs: {},
    });
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.phaser.destroy(true);
    this.commonService.loadingChange(false);
  }

  public fetchAndSetZoom(): void {
    (this.phaser.scene.getScenes(true)[0] as any)?.fetchAndSetMinimalZoom();
  }

  @HostListener('document:keyup', ['$event'])
  private onKeyup(event: any) {
    if (this.popupsManagerService.currentPopup && event.key === 'Escape') {
      this.popupsManagerService.hide();
    }
  }

  private calculateWallSize(): Size {
    const rect = this.container.nativeElement.getBoundingClientRect();
    return {
      w: rect.width,
      h: rect.height,
    };
  }

  private updateCanvasSize(): void {
    if (this.phaser) {
      this.phaser.scale.resize(this.size.w, this.size.h);
    }
  }

  private init(): void {
    this.size = this.calculateWallSize();
    this.phaser = new Phaser.Game({
      type: Phaser.WEBGL,
      scale: {
        mode: Phaser.Scale.NONE,
        parent: 'wall-container',
        width: this.size.w,
        height: this.size.h,
      },
      scene: [MainScene],
    });
    this.toolboxActionService.initGame(this.phaser);
  }

  private subscribeToEvents(): void {
    Events.on(EventKey.TileClick)
      .pipe(takeUntil(this.destroy$))
      .subscribe((props: any) => {
        if (!this.authService.publicId) {
          this.authService.requestSignIn();
        } else {
          this.openPurchaseFlow(props?.data);
        }
      });
    Events.on(EventKey.ItemSelected)
      .pipe(takeUntil(this.destroy$))
      .subscribe((props: any) => {
        const nftInfo = { ...props?.data?.cartItem };
        if (!!props?.data?.tileId) {
          nftInfo.tiles = [
            props?.data?.cartItem?.tiles.find(
              (ci: any) => ci.id === props?.data.tileId
            ),
          ];
        }
        this.popupsManagerService.show(NftInformationComponent, {
          className: props?.data?.clickedLeftSide ? 'popup-right' : '',
          inputs: {
            nftInfo,
          },
        });
      });
    Events.on(EventKey.WallLoaded)
      .pipe(
        switchMap(() =>
          combineLatest([
            this.cartService.cartItems$,
            this.cartService.wallItems$,
          ])
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(([cartItems, wallItems]: ComplexWallItem[][]) => {
        Events.emit({
          key: EventKey.CartData,
          data: {
            cartItems: [...cartItems, ...wallItems],
          },
        });
      });
    Events.on(EventKey.WallDataIsLoading)
      .pipe(takeUntil(this.destroy$))
      .subscribe((props: any) =>
        this.commonService.loadingChange(props.data.loading)
      );
  }
}
